import { Button } from '@mui/material'
import { Form, Formik, FormikValues, useField } from 'formik'
import React, { PropsWithChildren, useEffect, useMemo } from 'react'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import {
  ChannelType,
  CostDetailsDimensions,
  GraphGranularity,
  Network,
  useGetCompanyDetailsQuery,
} from '../../../__generated__/graphql'
import SelectField from '../../form/SelectField'
import * as Yup from 'yup'
import { getMatchingTimeframe } from './StatsDateRange'
import dayjs from 'dayjs'
import { PerformanceStatsTimeFilters } from './useStatsFilterStorage'
import { ChartOptionsType } from '../../../utils/hooks/performanceStats/usePerformanceStats'
import { getChartDateFormat } from '../../../utils/hooks/performanceStats/chartDateFormat'
import { useCurrentUser, useIsPwsUser } from '../../../utils/hooks/useUserContext'
import { useParams } from 'react-router-dom'
import { AllRoute } from '../../../pages/routeParams'
import { PWS_RESELLER_ID } from '../../../utils/hooks/reseller/useIsPowerspaceReseller'
import StatsDateFilter from './StatsDateFilter'
import { SelectOptions } from '../../form/Options'
import { getAvailableGranularities } from './AvailableGranularities'
import { useUserLocalConfig } from '../../../utils/hooks/useLocalStorage'
import BadStatsDateFilter2 from './BadStatsDateFilter2'
import StatsFiltersSettings from './StatsFiltersSettings'
import Toolbar from '../../toolbar/Toolbar'
import ToolbarFilterRow, { ToolbarFilterField } from '../../toolbar/ToolbarFilterRow'

export const StatsFiltersWidth = 200

export const StatsToolbarFilterField = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <ToolbarFilterField minWidth={StatsFiltersWidth}>{children}</ToolbarFilterField>
)

export type StatsFiltersProps<T extends PerformanceStatsTimeFilters> = {
  filters: T
  onChange: (update: T) => void
  setChartDateFormat: React.Dispatch<React.SetStateAction<string>>
  chartOptions: ChartOptionsType
  setChartOptions: React.Dispatch<React.SetStateAction<ChartOptionsType>>
  performanceStatsDimensions?: CostDetailsDimensions[]
  settingsExtra?: React.ReactNode
  toolbarEnd?: React.ReactNode
  isAutoReloading?: boolean
  withGranularity?: boolean
}

export const StatsFilters = <T extends PerformanceStatsTimeFilters>({
  filters,
  onChange,
  setChartDateFormat,
  chartOptions,
  setChartOptions,
  children,
  performanceStatsDimensions,
  settingsExtra,
  toolbarEnd,
  isAutoReloading = false,
  withGranularity = true,
}: PropsWithChildren<StatsFiltersProps<T>>): JSX.Element => {
  const { displayValues } = chartOptions
  const t = useAppTranslations()

  const [userConfig] = useUserLocalConfig()

  const otherFilters = {
    ...(performanceStatsDimensions ?? []).reduce(
      (dimensions, dimension) => Object.assign({}, dimensions, { [dimension]: [] }),
      {} as Record<CostDetailsDimensions, string[]>
    ),
    ...filters.otherFilters,
  }

  const initialValues = {
    ...filters,
    otherFilters,
    timeframe: getMatchingTimeframe(filters.startDate, filters.endDate),
  }

  const validationSchema = Yup.object().shape({
    startDate: Yup.date(),
    endDate: Yup.date(),
  })

  const onFilterSubmit = (values: FormikValues): void => {
    setChartDateFormat(
      getChartDateFormat(
        values.granularity as GraphGranularity,
        values.startDate as string,
        values.endDate as string,
        values.timeZone as string
      )
    )
    const endDateIsToday = dayjs(values.endDate).isSame(dayjs(), 'day')
    onChange({
      ...filters,
      ...values,
      endDate: endDateIsToday ? dayjs().format() : values.endDate,
    })
  }

  const handleDisplayValuesSwitch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setChartOptions({ ...chartOptions, displayValues: event.target.checked })
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onFilterSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {(formik) => {
        const endDateIsNotToday = !dayjs().isSame(formik.values.endDate, 'day')

        return (
          <Form>
            <Toolbar
              alwaysVisible
              end={
                <>
                  {toolbarEnd}
                  <StatsFiltersSettings
                    displayValues={displayValues}
                    extraSettings={settingsExtra}
                    isRefetching={isAutoReloading}
                    onDisplayValuesChange={handleDisplayValuesSwitch}
                  />
                  <Button variant="contained" type="submit" size="small" disabled={!formik.dirty && endDateIsNotToday}>
                    {t('commons.actions.filter')}
                  </Button>
                </>
              }
              start={
                <ToolbarFilterRow>
                  {!userConfig.tester && <StatsDateFilter withGranularity={withGranularity} />}

                  {userConfig.tester && <BadStatsDateFilter2 withGranularity={withGranularity} />}

                  {(performanceStatsDimensions ?? []).map((dimension) => (
                    <Dimension key={dimension.toString()} dimension={dimension} />
                  ))}

                  {children}
                </ToolbarFilterRow>
              }
              style="stats"
            />
          </Form>
        )
      }}
    </Formik>
  )
}

export const AvailableGranularity = (): JSX.Element => {
  const t = useAppTranslations()
  const name = 'granularity'

  const [{ value: startDateString }] = useField({ name: 'startDate' })
  const [{ value: endDateString }] = useField({ name: 'endDate' })
  const [, , { setValue: setGranularity }] = useField({ name: 'granularity' })

  const granularityList = useMemo(
    () => getAvailableGranularities(dayjs(startDateString), dayjs(endDateString)),
    [startDateString, endDateString]
  )

  useEffect(() => {
    const lowerGranularity = granularityList.find(({ disabled }) => !disabled)

    setGranularity(lowerGranularity?.granularity ?? GraphGranularity.Year)
  }, [granularityList])

  return (
    <SelectField
      name={name}
      label={'stats.filter.granularity'}
      withNone={false}
      variant="outlined"
      options={granularityList.map(({ granularity, disabled }) => ({
        value: granularity,
        label: t(`stats.granularity.${granularity}`),
        disabled,
      }))}
    />
  )
}

export const Dimension = ({ dimension }: { dimension: CostDetailsDimensions }): JSX.Element | null => {
  const isPwsUser = useIsPwsUser()
  const t = useAppTranslations()
  const PwsName = 'Powerspace SAS'
  const user = useCurrentUser()
  const { slug } = useParams<AllRoute>()
  const { data: companyData } = useGetCompanyDetailsQuery({
    variables: { slug },
    skip: !slug || dimension === CostDetailsDimensions.Channel,
  })

  const options = useMemo((): SelectOptions[] => {
    const opts: { value: string; label: string }[] = []
    switch (dimension) {
      case CostDetailsDimensions.Channel:
        opts.push(
          ...Object.values(ChannelType).map((value) => ({
            value: value.toString(),
            label: t(`enum.ChannelType.${value}`),
          }))
        )
        break

      case CostDetailsDimensions.Network:
        opts.push(
          ...Object.values(Network).map((value) => ({
            value: value.toString(),
            label: t(`enum.Network.${value}`),
          }))
        )
        break

      case CostDetailsDimensions.Market:
        opts.push({ value: 'FR-FR', label: 'Fr' }, { value: 'IT-IT', label: 'It' })
        break

      case CostDetailsDimensions.DemandSource:
        if (!slug && user.me.company.id !== PWS_RESELLER_ID)
          opts.push(
            { value: user.me.company.id, label: user.me.company.name },
            { value: PWS_RESELLER_ID, label: PwsName }
          )

        if (companyData?.company && companyData.company.reseller.id !== PWS_RESELLER_ID)
          opts.push(
            {
              value: companyData.company.reseller.id,
              label: companyData.company.reseller.name,
            },
            { value: PWS_RESELLER_ID, label: PwsName }
          )
        break

      default:
        break
    }

    return opts
  }, [dimension, slug, user, companyData])

  if (
    !isPwsUser &&
    (dimension === CostDetailsDimensions.Channel ||
      dimension === CostDetailsDimensions.Market ||
      dimension === CostDetailsDimensions.Network)
  )
    return null

  if (!options.length) return null

  return (
    <StatsToolbarFilterField>
      <SelectField
        name={`otherFilters.${dimension}`}
        label={`stats.filter.${dimension}`}
        noneTranslation="commons.all"
        variant="outlined"
        multiple
        options={options}
      />
    </StatsToolbarFilterField>
  )
}
